import * as THREE from 'three';

/** 세로형 9:16 기준. 기획서 화면 규격. */
const REFERENCE_ASPECT = 9 / 16;
const BASE_FOV = 50;

export class Stage {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly resizeObserver: ResizeObserver;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.scene.background = new THREE.Color(0x9fd6f0);
    this.scene.fog = new THREE.Fog(0x9fd6f0, 22, 48);

    // 낙하 구간 전체(바닥 y≈0.5 ~ 시작 y≈6.5)가 화면에 들어오도록 잡는다.
    // 페이지를 열자마자 아무것도 보이지 않으면 배포가 깨진 것으로 오해하게 된다.
    this.camera = new THREE.PerspectiveCamera(BASE_FOV, 1, 0.1, 100);
    this.camera.position.set(0, 4.2, 11);
    this.camera.lookAt(0, 3.2, 0);

    this.setupLights();

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    this.resizeObserver.disconnect();
    this.renderer.dispose();
  }

  private setupLights(): void {
    // 카툰 톤이라 그림자를 깊게 깔지 않는다. 기획서 3-2의 말랑한 감정 톤에 맞춘다.
    const ambient = new THREE.HemisphereLight(0xffffff, 0xb08968, 1.5);
    this.scene.add(ambient);

    const sun = new THREE.DirectionalLight(0xfff4e0, 2.1);
    sun.position.set(4, 9, 6);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 30;
    sun.shadow.camera.left = -10;
    sun.shadow.camera.right = 10;
    sun.shadow.camera.top = 10;
    sun.shadow.camera.bottom = -10;
    sun.shadow.bias = -0.0008;
    this.scene.add(sun);
  }

  /**
   * 세로형 게임이라 화면이 기준(9:16)보다 더 좁아지면 수평 시야가 잘린다.
   * 그 경우 수직 fov를 넓혀 게임 영역이 항상 화면에 들어오게 보정한다.
   */
  private resize(): void {
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    const aspect = width / height;

    this.camera.aspect = aspect;

    if (aspect < REFERENCE_ASPECT) {
      const baseHalfTan = Math.tan(THREE.MathUtils.degToRad(BASE_FOV) / 2);
      const correctedHalfTan = (baseHalfTan * REFERENCE_ASPECT) / aspect;
      this.camera.fov = THREE.MathUtils.radToDeg(Math.atan(correctedHalfTan) * 2);
    } else {
      this.camera.fov = BASE_FOV;
    }

    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(width, height, false);
  }
}
